import { Head, Link, router, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'
import { AreaType } from './types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import Form from './form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {DollarSign, MapPin, Plus, Users} from "lucide-react";

interface IndexProps {
  areas: AreaType[]
  communities: {
    id: string
    name: string
  }[]
}

export default function Index({ areas, communities }: IndexProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingArea, setEditingArea] = useState<AreaType | null>(null)
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('status') ?? 'all'
  })

  const emptyArea = useMemo(
    () => ({
      id: '',
      name: '',
      community_id: '',
      code: '',
      metadata: '',
      status: 'active',
      description:"",
    }),
    []
  )

  const isEditing = Boolean(editingArea)
  const formArea = editingArea ?? emptyArea
  const formAction = isEditing ? `/system/areas/${formArea.id}` : '/system/areas'
  const formMethod = isEditing ? 'patch' : 'post'
  const formTitle = isEditing ? 'Edit area' : 'New area'
  const formSubmitText = isEditing ? 'Update Area' : 'Create Area'

  const openCreateDialog = () => {
    setEditingArea(null)
    setDialogOpen(true)
  }

  const openEditDialog = (area: AreaType) => {
    setEditingArea(area)
    setDialogOpen(true)
  }

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
      setEditingArea(null)
    }
  }, [flash?.notice])

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/system/areas',
        {
          q: searchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['areas'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, statusFilter])

  return (
    <>
      <Head title="Areas" />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Area Management</h1>
            <p className="text-sm text-muted-foreground">Manage collection areas and assign them to agents</p>
          </div>

            <Button onClick={openCreateDialog} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" />
              Add Area
            </Button>

        </div>
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Areas</p>
                <p className="mt-2 text-3xl font-bold text-foreground">0</p>
              </div>
              <MapPin className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxpayers</p>
                <p className="mt-2 text-3xl font-bold text-foreground">0</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Collections</p>
                <p className="mt-2 text-3xl font-bold text-success">0 FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Areas</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{areas.filter((a) => a.status === "active").length}</p>
              </div>
              <MapPin className="h-8 w-8 text-success" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mt-6 grid gap-4 md:grid-cols-[1fr_200px]">
          <Input
            placeholder="Search by area name"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        </div>

        {/* Areas Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {areas.map((area) => (
              <Link key={area.id} href={`/system/areas/${area.id}`}>
                <div className="rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-primary/10 p-2">
                          <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">{area.name}</h3>
                          <p className="text-sm text-muted-foreground">{area.code}</p>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">{area.description}</p>
                    </div>
                    <span className="inline-flex rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">Active</span>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-4 border-t border-border pt-6">
                    <div>
                      <p className="text-xs text-muted-foreground">Assigned Agent</p>
                      <p className="mt-1 font-medium text-foreground">0</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Taxpayers</p>
                      <p className="mt-1 font-medium text-foreground">0</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Collections</p>
                      <p className="mt-1 font-medium text-success">0</p>
                    </div>
                  </div>
                </div>
              </Link>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{formTitle}</DialogTitle>
          </DialogHeader>
          <Form
            key={`${isEditing ? formArea.id : 'new'}`}
            area={formArea}
            communities={communities}
            action={formAction}
            method={formMethod}
            submitText={formSubmitText}
            onSuccess={() => {
              setDialogOpen(false)
              setEditingArea(null)
            }}
          />
        </DialogContent>
      </Dialog>

    </>
  )
}
